import React, {useEffect, useState} from "react";
import useAxios from "../../utils/useAxios";
import {useTranslation} from "react-i18next";
import FeatherIcon from "feather-icons-react";
import ErrorMessage from "../../components/ErrorMessage";
import Skeleton from "react-loading-skeleton";
import {toast} from "react-toastify";
import SelectInput from "../../components/SelectInput";

const initialData = {
    name: "",
    club: "",
    start_date: "",
    end_date: "",
    fixed_points_coefficient: "",
};

const RatingForm = ({rating = null, onSuccess, onClose, clubs}) => {
    const {t} = useTranslation();
    const api = useAxios();

    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(true);

    // --- Load rating data if editing ---
    useEffect(() => {
        if (rating?.id) {
            api.get(`/api/v1.0/ratings/${rating.id}/`)
                .then(res =>
                    setFormData({
                        name: res.data.name || "",
                        club: res.data.club || "",
                        start_date: res.data.start_date?.slice(0, 10) || "",
                        end_date: res.data.end_date?.slice(0, 10) || "",
                        fixed_points_coefficient: res.data.fixed_points_coefficient ?? "",
                    })
                )
                .catch(console.error)
                .finally(() => setLoader(false));
        } else {
            setLoader(false);
        }
    }, [rating]);

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setErrors({});

        try {
            const dataToSend = {};
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== "" && value !== null && value !== undefined) {
                    dataToSend[key] = value;
                }
            });

            if (!dataToSend.club && clubs.length === 1) {
                dataToSend.club = clubs[0].id;
            }
            const response = rating?.id
                ? await api.patch(`/api/v1.0/ratings/${rating.id}/`, dataToSend)
                : await api.post(`/api/v1.0/ratings/`, dataToSend);

            toast.success(
                t(rating?.id ? "Data updated successfully" : "Rating created successfully")
            );

            onSuccess?.(response.data);
            onClose?.();
        } catch (error) {
            setErrors(error.response?.data || {});
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t("Are you sure you want to delete this rating?"))) return;

        try {
            await api.delete(`/api/v1.0/ratings/${rating.id}/`);
            toast.success(t("Rating deleted successfully"));
            onSuccess?.();
            onClose?.();
        } catch (error) {
            toast.error(t("Error deleting rating"));
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <h2>
                <FeatherIcon icon="bar-chart-2"/>
                {rating?.id ? t("Edit rating") : t("Create rating")}
            </h2>

            <div className="form-group">
                <label>{t("Name")}</label>
                {loader ? <Skeleton height={42}/> : (
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-control"
                    />
                )}
                <ErrorMessage error={errors.name && t(errors.name)}/>
            </div>
            {
                clubs.length > 1 &&
                <div className="form-group">
                    <SelectInput
                        default_text={t("Choose a club")}
                        label_text={t("Club")}
                        data={clubs.map(club => ({id: club.id, name: club.name}))}
                        default_value={formData.club || ""}
                        input_name="club"
                        setInputValue={val => setFormData(prev => ({...prev, club: val}))}
                        search={clubs.length > 5}
                    />
                    <ErrorMessage error={errors.club && t(errors.club)}/>
                </div>
            }
            <div className="form-group">
                <label>{t("Start date")}</label>
                {loader ? <Skeleton height={42}/> : (
                    <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        className="form-control"
                        onClick={e => e.target.showPicker?.()}
                    />
                )}
                <ErrorMessage error={errors.start_date && t(errors.start_date)}/>
            </div>

            <div className="form-group">
                <label>{t("End date")}</label>
                {loader ? <Skeleton height={42}/> : (
                    <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        className="form-control"
                        onClick={e => e.target.showPicker?.()}
                    />
                )}
                <ErrorMessage error={errors.end_date && t(errors.end_date)}/>
            </div>

            {/* Used only by tournaments whose "Auto (fixed points)" rating mode
                is linked to this rating (TournamentRatingSettings.js) - it
                multiplies that site-wide place->points base curve, shared by
                every such tournament instead of being set per-tournament. */}
            <div className="form-group">
                <label>{t("Points coefficient")}</label>
                {loader ? <Skeleton height={42}/> : (
                    <input
                        type="number"
                        step="0.1"
                        name="fixed_points_coefficient"
                        value={formData.fixed_points_coefficient}
                        onChange={handleChange}
                        className="form-control"
                    />
                )}
                <ErrorMessage error={errors.fixed_points_coefficient && t(errors.fixed_points_coefficient)}/>
            </div>
            <br/>
            <ul className="tabs">
                <li>
                    <a href="#" className="action-button" onClick={e => {
                        e.preventDefault();
                        e.currentTarget.closest("form").requestSubmit();
                    }}>
                        <FeatherIcon icon="check"/> {t("Save")}
                    </a>
                </li>
                {rating?.id && (
                    <li>
                        <a href="#" className="action-button btn-green" onClick={handleDelete}>
                            <FeatherIcon icon="trash-2"/> {t("Delete")}
                        </a>
                    </li>
                )}
            </ul>
        </form>
    );
};

export default RatingForm;
